// ---------------------------------------------------------------------------
// themeContrast — a aritmética de contraste que o portão do painel usa.
//
// Blueprint: `docs/systems/admin-themes.md`.
//
// ## Por que existe, e por que é COMPOSTO
//
// Um tema criado no admin vai ao ar sem passar por ninguém. Sem portão, a
// primeira paleta bonita e ilegível fica no app de quem a comprar. Este módulo
// é o que transforma "achei bonito" em "passa no AA".
//
// A composição é obrigatória, não zelo: superfície pode ter alfa (a Lua tem), e
// a camada ambiente muda o fundo efetivo atrás do texto. Medir a cor DECLARADA
// aprovaria um tema que reprova na tela — e tratar alfa como opaco é a
// armadilha reincidente registrada no `CLAUDE.md`.
//
// ⚠️ Toda medição deve começar pela contraprova (`selfCheck`). Um medidor que
// aprova tudo não discrimina nada, e um que reprova tudo faz o operador
// desligar o portão.
// ---------------------------------------------------------------------------

export type Rgba = readonly [number, number, number, number];

/** Aceita `#rrggbb`, `rgb()` e `rgba()`. Devolve `null` no que não entende. */
export function parseColor(input: string | undefined | null): Rgba | null {
  const c = (input ?? "").trim();
  const hex = /^#([0-9a-f]{6})$/i.exec(c);
  if (hex) {
    const n = Number.parseInt(hex[1], 16);
    return [n >> 16, (n >> 8) & 255, n & 255, 1];
  }
  const fn = /^rgba?\(([^)]+)\)$/i.exec(c);
  if (fn) {
    const p = fn[1].split(",").map((s) => Number.parseFloat(s));
    if (p.length < 3 || p.some((v) => Number.isNaN(v))) return null;
    return [p[0], p[1], p[2], p.length > 3 ? p[3] : 1];
  }
  return null;
}

/** `fg` composto sobre `bg`. O resultado é sempre opaco. */
export function composite(fg: Rgba, bg: Rgba): Rgba {
  const a = fg[3];
  return [
    fg[0] * a + bg[0] * (1 - a),
    fg[1] * a + bg[1] * (1 - a),
    fg[2] * a + bg[2] * (1 - a),
    1,
  ];
}

/** Luminância relativa WCAG. */
export function luminance([r, g, b]: Rgba): number {
  const f = (v: number) => {
    const x = v / 255;
    return x <= 0.04045 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

/** Razão de contraste WCAG entre duas cores JÁ opacas. */
export function contrastRatio(a: Rgba, b: Rgba): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Contraste de uma tinta (que pode ter alfa) sobre um fundo, com camadas.
 *
 * `layers` são pintadas em ordem sobre `base` antes da tinta — é assim que a
 * camada ambiente e uma superfície translúcida entram na conta.
 */
export function inkContrast(ink: Rgba, base: Rgba, layers: readonly Rgba[] = []): number {
  let fundo = base;
  for (const l of layers) fundo = composite(l, fundo);
  return contrastRatio(composite(ink, fundo), fundo);
}

/**
 * 🔴 Contraprova do medidor. Roda ANTES de qualquer número valer.
 *
 * Preto sobre branco tem de dar 21 exato, e uma cor contra si mesma, 1. Um
 * medidor quebrado que devolve números plausíveis é pior que nenhum: ele
 * aprova temas ilegíveis com autoridade.
 */
export function selfCheck(): { ok: boolean; blackOnWhite: number; sameColor: number } {
  const bw = contrastRatio([0, 0, 0, 1], [255, 255, 255, 1]);
  const same = contrastRatio([128, 128, 128, 1], [128, 128, 128, 1]);
  return {
    ok: Math.abs(bw - 21) < 0.005 && Math.abs(same - 1) < 0.005,
    blackOnWhite: bw,
    sameColor: same,
  };
}

export interface ContrastCheck {
  /** Chave estável, para o teste e para a UI destacar a linha. */
  id: string;
  /** O que o operador precisa entender que está errado. */
  label: string;
  ratio: number;
  target: number;
  passes: boolean;
}

export interface ContrastReport {
  checks: ContrastCheck[];
  /** Menor razão medida. */
  floor: number;
  failures: ContrastCheck[];
  /** `false` bloqueia a publicação. */
  passes: boolean;
  measuredAt: string;
}

/**
 * Os pares que um tema PRECISA acertar para ir ao ar.
 *
 * ⚠️ A lista inclui **tinta sobre o preenchimento do accent**, e essa entrada
 * nasceu de um defeito real: a passada de contraste dos temas sazonais mediu o
 * accent contra o FUNDO e nunca a tinta sobre o accent, e o Sol foi ao código
 * com 4,24 ali — abaixo do AA, no rótulo do botão primário. O que não é medido
 * não é garantido.
 *
 * `--border-strong` fica FORA da lista de propósito: medido, ele reprova 3:1
 * nos 26 temas do catálogo (grifo 1,56 · Linho 1,55). É convenção da casa, e um
 * portão que reprova todo tema existente seria desligado no primeiro uso.
 */
const ALVO_TEXTO = 4.5;
const ALVO_GRAFICO = 3;

export function gradeTokens(tokens: Record<string, string>): ContrastReport {
  const cor = (k: string, queda?: Rgba): Rgba => parseColor(tokens[k]) ?? queda ?? [0, 0, 0, 1];

  const base = cor("--surface-base", [255, 255, 255, 1]);
  const ambiente = parseColor(pickAmbientPeak(tokens["--theme-ambient"]));
  const camadasFundo = ambiente ? [ambiente] : [];
  // A superfície elevada compõe SOBRE o fundo já com ambiente: é assim que ela
  // aparece na tela quando tem alfa.
  const raisedRaw = cor("--surface-raised", base);
  const camadasCard = [...camadasFundo, raisedRaw];

  const accent = cor("--accent-brand");

  const checks: ContrastCheck[] = [
    {
      id: "text-primary-on-base",
      label: "Texto principal sobre o fundo",
      ratio: inkContrast(cor("--text-primary"), base, camadasFundo),
      target: ALVO_TEXTO,
      passes: false,
    },
    {
      id: "text-primary-on-raised",
      label: "Texto principal sobre o card",
      ratio: inkContrast(cor("--text-primary"), base, camadasCard),
      target: ALVO_TEXTO,
      passes: false,
    },
    {
      id: "text-secondary-on-base",
      label: "Texto secundário sobre o fundo",
      ratio: inkContrast(cor("--text-secondary"), base, camadasFundo),
      target: ALVO_TEXTO,
      passes: false,
    },
    {
      id: "text-secondary-on-raised",
      label: "Texto secundário sobre o card",
      ratio: inkContrast(cor("--text-secondary"), base, camadasCard),
      target: ALVO_TEXTO,
      passes: false,
    },
    {
      // A entrada que faltava e deixou o Sol passar com 4,24.
      id: "text-on-accent",
      label: "Rótulo do botão sobre o accent",
      ratio: inkContrast(cor("--text-on-accent"), accent),
      target: ALVO_TEXTO,
      passes: false,
    },
    {
      id: "text-muted-on-raised",
      label: "Texto apagado sobre o card",
      ratio: inkContrast(cor("--text-muted"), base, camadasCard),
      target: ALVO_GRAFICO,
      passes: false,
    },
    {
      id: "accent-on-base",
      label: "Accent sobre o fundo",
      ratio: inkContrast(accent, base, camadasFundo),
      target: ALVO_GRAFICO,
      passes: false,
    },
    // ⚠️ O veredito usa o valor ARREDONDADO, o mesmo que a tela mostra. Decidir
    // pelo bruto faria o painel exibir "4.5" ao lado de uma reprovação (o valor
    // real seria 4,497), e o operador não teria como entender o que consertar.
    // O servidor usa a mesma regra — travado por teste de paridade.
  ].map((c) => {
    const ratio = round2(c.ratio);
    return { ...c, ratio, passes: ratio >= c.target };
  });

  const failures = checks.filter((c) => !c.passes);
  return {
    checks,
    floor: round2(Math.min(...checks.map((c) => c.ratio))),
    failures,
    passes: failures.length === 0,
    measuredAt: new Date().toISOString(),
  };
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * O pico do gradiente ambiente: a primeira parada, que é a de maior alfa.
 *
 * Pessimista de propósito — é o ponto da tela onde o fundo mais se aproxima da
 * tinta, e portanto onde o texto tem menos contraste. Aprovar pelo caso médio
 * deixaria passar um tema que só falha no topo, que é justamente onde moram o
 * cabeçalho e a saudação.
 */
export function pickAmbientPeak(gradient: string | undefined): string | null {
  if (!gradient) return null;
  const stops = gradient.match(/rgba?\([^)]+\)/g);
  return stops?.[0] ?? null;
}
